import { differenceInCalendarDays } from "date-fns";
import type { Account } from "@um/contracts";
import type { EntitlementIdentifier, SubscriberAttributes } from "./contract";
import type { SubscriptionLifecycleStatus } from "./types";

/**
 * Subscription entitlement tiers shown in the UI, ordered for display.
 * `basic` (Character Support) is the base tier and is always listed first.
 */
export const SUBSCRIPTION_ENTITLEMENTS: EntitlementIdentifier[] = [
  "basic",
  "clinical_support",
];

const ENTITLEMENT_DISPLAY_NAMES: Record<EntitlementIdentifier, string> = {
  basic: "Character Support",
  clinical_support: "Clinical Support",
};

/** Human-friendly tier name for an entitlement (e.g. `basic` → "Character Support"). */
export function entitlementDisplayName(id: EntitlementIdentifier): string {
  return ENTITLEMENT_DISPLAY_NAMES[id] ?? id;
}

/** Capacitor RevenueCat plugin returns ISO date strings; convert at the native boundary only. */
export function dateFromNativeRevenueCatString(
  value: string | null | undefined
): Date | null {
  if (value == null || value === "") return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : new Date(ms);
}

/** RevenueCat web SDK uses lowercase `Store` values; native uses `SCREAMING_SNAKE_CASE`. */
export function normalizePurchaseStore(
  store: string | null | undefined
): string | null {
  if (store == null) return null;
  const s = String(store).trim();
  if (!s) return null;
  return s.toUpperCase();
}

export function deriveLifecycleStatus(
  info: {
    isActive: boolean;
    willRenew: boolean;
    refundedAt: Date | null;
    billingIssuesDetectedAt: Date | null;
    gracePeriodExpiresDate: Date | null;
  },
  nowMs: number
): SubscriptionLifecycleStatus {
  if (info.refundedAt != null) return "refunded";

  const graceEndMs = info.gracePeriodExpiresDate?.getTime() ?? NaN;
  if (info.isActive && !Number.isNaN(graceEndMs) && graceEndMs > nowMs) {
    return "grace_period";
  }

  if (info.billingIssuesDetectedAt != null && info.isActive) {
    return "billing_issue";
  }

  if (!info.isActive) return "expired";
  if (!info.willRenew) return "active_non_renewing";
  return "active";
}

/**
 * Label from the store product id: segment before the first `_` or `:`, then
 * sentence-case the segment (`spark_foo` → `Spark`, `spark:foo-bar` → `Spark`).
 */
export function displayNameFromProductIdentifier(productId: string): string {
  const trimmed = productId.trim();
  if (!trimmed) return "";
  const segment = trimmed.split(/[:_]/, 2)[0] ?? trimmed;
  const base = segment || trimmed;
  return base.charAt(0).toUpperCase() + base.slice(1).toLowerCase();
}

/** Pick the subscription row with the latest period start, then latest expiry tie-breaker. */
export function sortSubscriptionEntriesByRecency<
  T extends { purchaseDateMs: number; expiresMs: number },
>(entries: [string, T][]): [string, T][] {
  return [...entries].sort((a, b) => {
    const d = b[1].purchaseDateMs - a[1].purchaseDateMs;
    if (d !== 0) return d;
    return b[1].expiresMs - a[1].expiresMs;
  });
}

/** Accounts with `daysOpen` at most this many calendar days are tagged `new`. */
const NEW_ACCOUNT_MAX_CALENDAR_DAYS = 7;

/**
 * Maps the Firestore account document into RevenueCat custom subscriber attributes.
 */
export function subscriberAttributesFromAccount(
  account: Account
): SubscriberAttributes {
  const createdAt = account.created?.toDate() || new Date();
  const days = Math.max(0, differenceInCalendarDays(new Date(), createdAt)) + 1;
  const daysWithApp: SubscriberAttributes["daysWithApp"] =
    days <= 7
      ? (days.toString() as SubscriberAttributes["daysWithApp"])
      : days <= 14
        ? "7-14"
        : "15+";

  const attribution = {
    utm_source:
      account.conversionData?.utm_source ||
      account.conversionData?.media_source,
    utm_campaign:
      account.conversionData?.utm_campaign || account.conversionData?.campaign,
    utm_id: account.conversionData?.utm_id,
    utm_medium: account.conversionData?.utm_medium,
  };

  return {
    ...(account.attributes || {}),
    ...Object.fromEntries(Object.entries(attribution).filter(([_, v]) => v)),
    daysWithApp,
    isInternal: account.isInternal ? "true" : "false",
    accountType: account.type,
    ...(account.testResourceGroup
      ? { testResourceGroup: account.testResourceGroup?.id }
      : {}),
  };
}

/**
 * Splits `placement` on `/` (empty segments dropped) and returns identifiers
 * from longest prefix to shortest, e.g. `a/b/c` → `["a/b/c","a/b","a"]`.
 */
function placementPathCandidates(placement: string): string[] {
  const segments = placement
    .split("/")
    .map((s) => s.trim())
    .filter(Boolean);
  if (segments.length === 0) return [];
  const out: string[] = [];
  for (let n = segments.length; n >= 1; n -= 1) {
    out.push(segments.slice(0, n).join("/"));
  }
  return out;
}

/**
 * Tries each placement prefix (longest first) until `getOfferingForPlacement`
 * returns a non-nullish offering.
 */
export async function resolveOfferingForPlacementPath<T>(
  placement: string,
  getOfferingForPlacement: (
    placementIdentifier: string
  ) => Promise<T | null | undefined>
): Promise<T | null> {
  for (const id of placementPathCandidates(placement)) {
    const offering = await getOfferingForPlacement(id);
    if (offering != null) return offering;
  }
  return null;
}
